import React from 'react';
import { reduxForm, getFormValues, initialize } from 'redux-form';
import { useSelector, useDispatch } from 'react-redux';
import moment from 'moment';
import globalStyles from 'common/global.module.scss';
import styles from './SearchForm.module.scss';
import { AppState } from 'store';
import { PropertyTab, NameTab, OtherTab, LinksTab, MultiTab } from './Tabs';
import {
  Form,
  Radio,
  Select,
  Button,
  Typography,
  Divider,
  Card,
  Icon,
  Row,
  Tooltip,
} from 'antd';
import { searchClear } from '../Search.actions';
import { FormField, required, defaultDateFormat, InputTypes } from 'common';
import SearchHistory from '../SearchHistory/SearchHistory';
import {
  Tabs,
  initialForm,
  locations,
  bookTypeOptions,
  NYC_DB_FORM_NAME,
} from './consts';
import { createSelector } from 'reselect';
import { compose } from 'redux';
import { Redirect } from 'react-router-dom';
import { useIsAuthenticated } from '@azure/msal-react';

const selector = createSelector(
  (state: AppState) => state.search.loading,
  (state: AppState) => getFormValues(NYC_DB_FORM_NAME)(state),
  (state: AppState) => state.login.isPublic,
  (loading, formValues, isPublic) => ({ loading, formValues, isPublic }),
);

const { Text } = Typography;
const { Option } = Select;

const SearchForm: React.FC = ({ change, handleSubmit, valid }: any) => {
  const dispatch = useDispatch();
  const {
    loading,
    formValues,
    isPublic,
  }: { loading: boolean; formValues: any; isPublic: boolean } =
    useSelector(selector);
  const isAuthenticated = useIsAuthenticated();

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>, name: string) => {
    switch (name) {
      case 'propertyTab.block':
      case 'propertyTab.lot':
      case 'propertyTab.ease':
        change('propertyTab.searchBy', 'parcel');
        break;
      case 'propertyTab.houseNumber':
      case 'propertyTab.street':
      case 'propertyTab.apartmentNumber':
        change('propertyTab.searchBy', 'address');
        break;
      case 'propertyTab.ownerName':
        change('propertyTab.searchBy', 'assessedOwner');
        break;
      case 'multiTab.block':
      case 'multiTab.lot':
      case 'multiTab.ease':
        change('multiTab.searchByBlockLot', true);
        break;
      default:
        break;
    }
    if (name.indexOf('nameTab.corporations') !== -1)
      change('nameTab.searchByCorporation', true);
    if (name.indexOf('nameTab.aliases') !== -1)
      change('nameTab.searchByIndividual', true);

    if (name.indexOf('multiTab.corporations') !== -1) {
      change('multiTab.searchByCorporation', true);
      change('multiTab.searchByName', true);
    }
    if (name.indexOf('multiTab.aliases') !== -1) {
      change('multiTab.searchByIndividual', true);
      change('multiTab.searchByName', true);
    }
    if (name.indexOf('multiTab.addresses') !== -1) {
      change('multiTab.searchByAddress', true);
    }
  };
  const login = useSelector((state: AppState) => state.login);
  if (login.user?.licenseflag == 'False') {
    return <Redirect to={`/pages/license`} />;
  }

  return (
    <Card size="small">
      <Form layout="vertical" onSubmit={handleSubmit}>
        <FormField
          label="Please select County to search"
          name="hob"
          validate={[required]}
          inputType={InputTypes.RADIO_GROUP}
          autoFocus={true}
        >
          {locations.map((item) => (
            <Radio key={item.value} value={item.value}>
              {item.name}
            </Radio>
          ))}
        </FormField>
        <FormField
          name="tab"
          inputType={InputTypes.RADIO_GROUP}
          buttonStyle="solid"
          className={'m-0'}
        >
          <Radio.Button value={Tabs.Property} className={styles.tab}>
            <Icon type="home" />
            &nbsp;Property search
          </Radio.Button>
          <Radio.Button value={Tabs.Name} className={styles.tab}>
            <Icon type="user" />
            &nbsp;Name search
          </Radio.Button>
          <Radio.Button value={Tabs.Other} className={styles.tab}>
            <Icon type="file" />
            &nbsp;Other search
          </Radio.Button>
          <Radio.Button value={Tabs.Multi} className={styles.tab}>
            <Icon type="database" />
            &nbsp;Multi search
          </Radio.Button>
          {isAuthenticated && !isPublic && (
            <Radio.Button value={Tabs.Links} className={styles.tab}>
              <Icon type="link" />
              &nbsp;Links
            </Radio.Button>
          )}
        </FormField>

        <SearchHistory changeFormValues={change} />

        {(() => {
          switch (formValues.tab) {
            case Tabs.Property:
              return <PropertyTab handleFieldFocus={handleFocus} />;
            case Tabs.Name:
              return (
                <NameTab
                  handleFieldFocus={handleFocus}
                  tabNamePrefix="nameTab"
                />
              );
            case Tabs.Other:
              return <OtherTab />;
            case Tabs.Multi:
              return <MultiTab handleFieldFocus={handleFocus} />;
            case Tabs.Links:
              return <LinksTab />;
            default:
              return null;
          }
        })()}

        <Divider dashed />
        {formValues.tab !== Tabs.Links && (
          <>
            <Text>Optional (For Judgments / Liens):</Text>
            <Row type="flex" align="middle">
              <FormField
                className={`${globalStyles.input} ${globalStyles.date}`}
                tabIndex="1700"
                label="From Date"
                name="from"
                inputType={InputTypes.DATE_PICKER}
                datepickervalue={moment(formValues.from, defaultDateFormat)}
              />
              <FormField
                className={`${globalStyles.input} ${globalStyles.date}`}
                tabIndex="1800"
                label="To Date"
                name="to"
                inputType={InputTypes.DATE_PICKER}
                datepickervalue={moment(formValues.to, defaultDateFormat)}
              />
              <FormField
                tabIndex="1900"
                label="Book Type"
                name="bookType"
                inputType={InputTypes.SELECT}
                className={globalStyles.select}
                allowClear={false}
                showSearch={true}
                filterOption={(input: any, option: any) =>
                  option.props.children
                    .toLowerCase()
                    .indexOf(input.toLowerCase()) >= 0
                }
              >
                {bookTypeOptions.map((item) => (
                  <Option key={item.value} value={item.value} title={item.text}>
                    {item.text}
                  </Option>
                ))}
              </FormField>
            </Row>

            <Button
              className={globalStyles.button}
              onClick={() => {
                dispatch(searchClear());
                dispatch(
                  initialize(NYC_DB_FORM_NAME, {
                    ...initialForm,
                    tab: formValues.tab,
                  }),
                );
              }}
              type="ghost"
            >
              Reset
            </Button>
            <Tooltip
              title={
                valid
                  ? 'Press to run search...'
                  : 'Please fill all the fields properly!'
              }
            >
              <Button
                icon="search"
                type="primary"
                htmlType="submit"
                disabled={loading || !valid}
              >
                Search
              </Button>
            </Tooltip>
            <Text style={{ color: '#40a9ff', fontWeight: 'bold' }}>
              &nbsp;&nbsp;*
            </Text>
            <Text>Fee applied on Search</Text>
            <Text style={{ color: '#40a9ff', fontWeight: 'bold' }}>*</Text>
          </>
        )}
      </Form>
    </Card>
  );
};

export default compose(
  reduxForm({
    form: NYC_DB_FORM_NAME,
    initialValues: initialForm,
  }),
)(React.memo(SearchForm));
